import { useEffect, useMemo, useRef, useState } from 'react';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useNavigate } from 'react-router-dom';
import { Callout } from '@/components/ui/callout';
import {
  BreakTab,
  ClampingTab,
  GeneralTab,
  HolderTab,
  LubTab,
  MachineTab,
  MaterialTab,
} from './FormTabs';
import type { MaterialTabHandle } from './FormTabs/MaterialTab';
import React from 'react';
import type { Trace } from '../type/Tool';
import type { TraceWriteRequest } from '../api/TraceabilityRequestType';
import type { Material } from '../type/Material';
import type { MaterialLot } from '../type/MaterialLot';
import { buildTracePayload } from '../hooks/useTraceFormBuilder';
import { useTraceSubmission } from '../hooks/useTraceSubmission';
import { TraceabilityFormSteps } from './TraceabilityFormSteps';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';

type TraceabilityFormProps = {
  toolId: string;
  tracabilityApiClient: TraceabilityApiClient;
  companyName: string;
  className?: string;
};

export const TraceabilityForm: React.FC<TraceabilityFormProps> = ({
  toolId,
  tracabilityApiClient,
  companyName,
  className = '',
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const trace = useRef<TraceWriteRequest>({
    trace: {} as Trace,
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { execute, cancel, retry, result, formSubmitted, setFormSubmitted } = useTraceSubmission(
    tracabilityApiClient,
    trace
  );

  const updateValue = (name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const stepKeys = ['general', 'material', 'machine', 'holder', 'clamping', 'lubrication', 'break'];

  const tabRefs = useMemo(
    () => stepKeys.map(() => React.createRef<{ validate: () => boolean }>()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const materialTabRef = useMemo(() => React.createRef<MaterialTabHandle>(), []);

  const steps = [
    {
      key: 'general',
      label: t('traceability.steps.general'),
      content: <GeneralTab ref={tabRefs[0]} values={formValues} onChange={updateValue} />,
    },
    {
      key: 'material',
      label: t('traceability.steps.material'),
      content: (
        <MaterialTab
          ref={materialTabRef}
          values={formValues}
          onChange={updateValue}
          traceabilityApiClient={tracabilityApiClient}
        />
      ),
    },
    {
      key: 'machine',
      label: t('traceability.steps.machine'),
      content: (
        <MachineTab
          ref={tabRefs[2]}
          companyName={companyName}
          values={formValues}
          onChange={updateValue}
        />
      ),
    },
    {
      key: 'holder',
      label: t('traceability.steps.holder'),
      content: <HolderTab ref={tabRefs[3]} values={formValues} onChange={updateValue} />,
    },
    {
      key: 'clamping',
      label: t('traceability.steps.clamping'),
      content: <ClampingTab ref={tabRefs[4]} values={formValues} onChange={updateValue} />,
    },
    {
      key: 'lubrication',
      label: t('traceability.steps.lubrication'),
      content: <LubTab ref={tabRefs[5]} values={formValues} onChange={updateValue} />,
    },
    {
      key: 'break',
      label: t('traceability.steps.break'),
      content: <BreakTab ref={tabRefs[6]} values={formValues} onChange={updateValue} />,
    },
  ];

  const isLastStep = (step: number) => step === steps.length - 1;

  useEffect(() => {
    setProgress(((currentStep + 1) / steps.length) * 100);
  }, [currentStep, steps.length]);

  const handleNext = () => {
    const isValid =
      currentStep === 1
        ? materialTabRef.current?.validate()
        : tabRefs[currentStep]?.current?.validate();
    if (!isValid) return;
    setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const handleSubmit = async (e: React.FormEvent | null) => {
    e?.preventDefault();

    if (e) {
      const isValid =
        currentStep === 1
          ? materialTabRef.current?.validate()
          : tabRefs[currentStep]?.current?.validate();
      if (!isValid) return;
    }
    // Last-resort guard: material must be set (MaterialTab.validate() already enforces this
    // at step 1, but this catches edge cases when the tab has been unmounted).
    const resolvedMaterial = (formValues.material as Material | null) ?? null;
    if (resolvedMaterial === null && currentStep !== 1) {
      // silently go back to material step so user can pick a material
      setCurrentStep(1);
      return;
    }

    setFormSubmitted(true);

    // material/supplier/lotNumber are lifted to formValues by MaterialTab on each change,
    // so they survive step unmount. Fall back to getValues() when still on step 1.
    const liveMatValues = materialTabRef.current?.getValues();
    const material: Material | null =
      liveMatValues?.material ?? resolvedMaterial;
    const supplier = (liveMatValues?.supplier ?? (formValues.materialLotSupplier as string)) || null;
    const lotNumber = (liveMatValues?.lotNumber ?? (formValues.materialLotNumber as string)) || null;

    let materialLot: MaterialLot | null = null;
    if (material?.id != null && (supplier || lotNumber)) {
      try {
        const lotRes = await tracabilityApiClient.materialLotFindOrCreate({
          materialId: material.id,
          supplier: supplier || null,
          lotNumber: lotNumber || null,
        });
        if (lotRes.ok) {
          materialLot = {
            id: lotRes.data.id,
            materialId: material.id,
            supplier: supplier || null,
            lotNumber: lotNumber || null,
          };
        }
      } catch (err) {
        console.error('Failed to find or create material lot:', err);
      }
    }

    trace.current = buildTracePayload(
      { ...formValues, material, materialLot },
      toolId,
      companyName
    );

    void execute();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && ['number', 'text'].includes((e.target as HTMLInputElement).type)) {
      if (!isLastStep(currentStep)) {
        handleNext();
        e.preventDefault();
      }
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    if (currentStep !== steps.length) {
      e.preventDefault();
    }
  };

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {formSubmitted && !result && (
        <Callout
          tone="neutral"
          loading
          action={
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full gap-2"
              onClick={cancel}
            >
              {t('traceability.form.cancel')}
            </Button>
          }
        >
          {t('traceability.form.sending')}
        </Callout>
      )}

      {formSubmitted && result?.ok === false && (
        <Callout
          tone="error"
          action={
            <>
              <Button
                type="button"
                variant="default"
                size="lg"
                className="flex-1 gap-2 sm:flex-none"
                onClick={retry}
              >
                {t('traceability.form.retry')}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="flex-1 gap-2 sm:flex-none"
                onClick={() => navigate('/')}
              >
                {t('traceability.form.backHome')}
              </Button>
            </>
          }
        >
          {result.message ?? result.name ?? t('traceability.form.errorDefault')}
        </Callout>
      )}

      {formSubmitted && result?.ok && (
        <Callout
          tone="success"
          action={
            <Button
              type="button"
              variant="default"
              size="lg"
              className="w-full gap-2"
              onClick={() => navigate('/traceability/scan')}
            >
              {t('traceability.form.newScan')}
            </Button>
          }
        >
          {t('traceability.form.success')}
        </Callout>
      )}

      {!formSubmitted && (
        <TraceabilityFormSteps
          steps={steps}
          currentStep={currentStep}
          progress={progress}
          sectionRefs={sectionRefs}
          isLastStep={isLastStep(currentStep)}
          onKeyDown={handleKeyDown}
          onSubmitForm={handleSubmitForm}
          onNext={handleNext}
          onPrevious={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
};
